import {Component, OnDestroy, OnInit} from "@angular/core";
import {Router} from "@angular/router";
import {FUISpinnersService} from "@fedelta-pos/ui";
import {ErrorHandlerService} from "../../../error-handler.service";
import {CustomerInstanceVenueService} from "../customer-instance-venue.service";
import {CustomerInstanceVenueSearchData} from "../customer-instance-venue-search.data";
import {DatePipe} from "@angular/common";
import {ContractService, SignerResponse} from "../contract/contract.service";
import {first, Subscription, timer} from "rxjs";

@Component({
    selector: "fhm-customer-instance-venue-manager-search-page",
    styleUrls: ["customer-instance-venue-manager-search-page.component.scss"],
    templateUrl: "./customer-instance-venue-manager-search-page.component.html",
    providers: [CustomerInstanceVenueService, ContractService],
    standalone: false
})
export class CustomerInstanceVenueManagerSearchPageComponent implements OnInit, OnDestroy {

    gridData: CustomerInstanceVenueSearchData[] = [];
    data: CustomerInstanceVenueSearchData[] = [];

    filterName: string;
    filterSalesRep: string;
    filterRequiresAction: string[];
    filterCreatedDateFrom: Date;
    filterCreatedDateTo: Date;

    noInstancesOnly: boolean;

    sortedColumn: string = "requiresContractDirectorSign";
    sortedColumnAscend: boolean = false;

    refreshSubscription: Subscription;

    constructor(
                private router: Router,
                private errorHandler: ErrorHandlerService,
                private spinnersService: FUISpinnersService,
                private customerInstanceVenueService: CustomerInstanceVenueService,
                private contractService: ContractService) {
    }

    ngOnInit(): void {
        this.load();
    }

    ngOnDestroy(): void {
        if (this.refreshSubscription) {
            this.refreshSubscription.unsubscribe();
        }
    }

    private initRefresh(): void {
        this.refreshSubscription = timer(5000).pipe(first()).subscribe(() => {
            this.loadData();
        });
    }

    async load(): Promise<void> {
        this.spinnersService.open();
        try {
            await this.loadData();
        } catch (error) {
            this.errorHandler.handleErrorResponse(error);
        } finally {
            this.spinnersService.close();
        }
    }

    async loadData(): Promise<void> {
        this.data = await this.customerInstanceVenueService.getCustomerInstanceVenues();
        this.updateFilter();
        this.initRefresh();
    }

    recordSelected(record: any): void {
        this.router.navigate(["/main/customer-instance-venue-edit/" + record["id"]], {queryParams: {signUp: true}});
    }

    updateFilter(): void {
        this.gridData = this.data.filter((record: CustomerInstanceVenueSearchData) => {
            const createdDate: Date = new Date(record.createdDate);

            if (this.filterName && record.name.toLowerCase().indexOf(this.filterName.toLowerCase()) === -1) {
                return false;
            } else if (this.filterSalesRep && (!record.salesRep || record.salesRep.toLowerCase().indexOf(this.filterSalesRep.toLowerCase()) === -1)) {
                return false;
            } else if (this.filterRequiresAction && this.filterRequiresAction.length > 0 &&
                this.filterRequiresAction.indexOf(this.getNextRequiredAction(record) ) === -1) {
                return false;
            } else if (record.discontinued) {
                return false;
            } else if (this.filterCreatedDateFrom && createdDate < this.filterCreatedDateFrom) {
                return false;
            } else if (this.filterCreatedDateTo && createdDate > this.filterCreatedDateTo) {
                return false;
            }
            return true;
        });

        // sort
        this.gridData.sort((a, b) => {
            if (this.sortedColumn) {
                if (this.sortedColumn === "requiresContractDirectorSign") {
                    return this.sortedColumnAscend ? (a.requiresContractDirectorSign ? 1 : 0) - (b.requiresContractDirectorSign ? 1 : 0)
                        : (b.requiresContractDirectorSign ? 1 : 0) - (a.requiresContractDirectorSign ? 1 : 0);
                } else {
                    return this.sortedColumnAscend ? (a[this.sortedColumn] || "").localeCompare(b[this.sortedColumn] || "")
                        : (b[this.sortedColumn] || "").localeCompare(a[this.sortedColumn] || "");
                }
            } else {
                return a.name.localeCompare(b.name);
            }
        });
    }

    async refresh(): Promise<void> {
        await this.load();
    }

    toDate(date: string): Date {
        return new Date(date.substr(6, 4) + "-" + date.substr(3, 2) + "-" + date.substr(0, 2));
    }

    styleColumnFn(columnName: string, record: CustomerInstanceVenueSearchData): any {
        if (columnName === "requiresContractDirectorSign") {
            return "link";
        } else if (columnName === "requiresAction") {
            if (record.requiresBilling || record.requiresContractSign || record.requiresAppPaymentAccount || record.requiresSalesHandover) {
                return {"color": "red", "font-weight": "bold"};
            } else {
                return {"color": "green", "font-weight": "bold"};
            }
        }
        return undefined;
    }

    valueColumnFn(columnName: string, record: CustomerInstanceVenueSearchData): any {
        if (columnName === "requiresAction") {
            if (record.requiresContractSign) {
                return "Contract Sign";
            }
            if (record.requiresContractDirectorSign) {
                return "";
            }
            if (record.requiresBilling) {
                return "Direct Debit";
            }
            if (record.requiresAppPaymentAccount) {
                return "Payment Account (Stripe)";
            }
            if (record.requiresSalesHandover) {
                return "Sales Handover";
            }
            return "";
        } else if (columnName === "signedDate") {
            if (record.signedDate) {
                return new DatePipe("en-GB").transform(new Date(record.signedDate), "dd/MM/yyyy");
            }
        } else if (columnName === "createdDate") {
            if (record.createdDate) {
                return new DatePipe("en-GB").transform(new Date(record.createdDate), "dd/MM/yyyy");
            }
        } else if (columnName === "contractSignedDate") {
            if (record.contractSignedDate) {
                return new DatePipe("en-GB").transform(new Date(record.contractSignedDate), "dd/MM/yyyy");
            }
        } else if (columnName === "requiresContractDirectorSign") {
            return record.requiresContractDirectorSign ? "Sign As Director" : "";
        }

        return record[columnName];
    }

    columnSorted(sorted): void {
        this.sortedColumn = sorted.column;
        this.sortedColumnAscend = sorted.ascend;
        if (!this.sortedColumn) {
            this.sortedColumn = "requiresContractDirectorSign";
            this.sortedColumnAscend = false;
        }
        this.updateFilter();
    }

    async signAsDirector(record: CustomerInstanceVenueSearchData): Promise<void> {
        this.spinnersService.open();
        const windowReference: Window = window.open();
        try {
            const resp: SignerResponse = await this.contractService.getDirectorSignerLink(record.id);
            this.spinnersService.close();
            windowReference.location.href = resp.url;
        } catch (e) {
            this.spinnersService.close();
            windowReference.close();
            this.errorHandler.handleErrorResponse(e);
        }
    }

    changeRequiresAction(requireActions: string[]): void {
        this.filterRequiresAction = requireActions;
        this.updateFilter();
    }

    getNextRequiredAction(record: CustomerInstanceVenueSearchData): string {
        if (record.requiresContractSign) {
            return "requiresContractSign";
        }
        if (record.requiresContractDirectorSign) {
            return "requiresContractDirectorSign";
        }
        if (record.requiresBilling) {
            return "requiresBilling";
        }
        if (record.requiresAppPaymentAccount) {
            return "requiresAppPaymentAccount";
        }
        if (record.requiresSalesHandover) {
            return "requiresSalesHandover";
        }
        return "";
    }
}
